import { Box, Grid } from "@chakra-ui/react";
import Joi from "joi";
import { useCallback, useEffect } from "react";

import {
  buildingCondition as buildingConditionConfig,
  buildingType as buildingTypeConfig,
  flatType as flatTypeConfig,
  locationType as locationTypeConfig,
  material as materialConfig,
  objectType as objectTypeConfig,
  ownership as ownershipConfig,
  propertyType as propertyTypeConfig,
  protectedArea as protectedAreaConfig,
  surroundingType as surroundingsConfig,
} from "@ud/config/prodeje/config";
import {
  IconArea,
  IconDog,
  IconHouse,
  IconKontrukce,
  IconPeople,
  IconStav,
} from "@ud/icons";
import { updateData } from "@ud/store/insertForm/insertSlice";
import IconDropdown from "components/Chakra/Icons/Dropdown";
import Select from "components/Chakra/Selectbox";
import StepButtons from "components/InsertOfferWrapper/components/StepButtons";
import { useTracking } from "contexts/Tracking";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import SectionCards from "../components/SectionCards";
import { SectionTitle } from "../components/SectionTitle";

type IDescription = {
  buttonNextText?: string;
  buttonPrevText?: string;
  handleNextStep: () => void;
};

type CustomFormValue = "ownership" | "flatType";

type IFormValue = {
  ownership: string;
  buildingType: string;
  condition: string;
  material: string;
  flatType: string;
  objectType: string;
  locationType: string;
  surroundings: string;
  protectedArea: string;
};

export const DescriptionFormTwo: React.FC<IDescription> = ({
  buttonNextText,
  buttonPrevText,
  handleNextStep,
}) => {
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();

  const insertReduxData = useAppSelector((e) => e.insertForm.data);

  const propertyType =
    (useAppSelector(
      (state) => state.insertForm.data.typeProperty,
    ) as keyof typeof propertyTypeConfig) || propertyTypeConfig.flat.id;

  const formSchema = Joi.object({
    ownership: Joi.string().custom((value, helpers) => {
      if (
        typeof value === "undefined" &&
        propertyType === propertyTypeConfig.flat.id
      ) {
        return helpers.error("any.invalid");
      }
    }),
    buildingType: Joi.string().custom((value, helpers) => {
      if (
        typeof value === "undefined" &&
        propertyType === propertyTypeConfig.house.id
      ) {
        return helpers.error("any.invalid");
      }
    }),
    condition: Joi.string().allow(""),
    material: Joi.string().allow(""),
    flatType: Joi.string(),
    objectType: Joi.string(),
    locationType: Joi.string(),
    surroundings: Joi.string(),
    protectedArea: Joi.string(),
  });

  const handleData: OnFormSubmitType<IFormValue> = ({
    condition,
    material,
    flatType,
    locationType,
    surroundings,
    objectType,
    protectedArea,
  }) => {
    dispatch(
      updateData({
        data:
          propertyType === propertyTypeConfig.flat.id
            ? {
                ownership: watchOwnership || undefined,
                condition: condition || undefined,
                material: material || undefined,
                flatType: flatType || undefined,
                locationType: locationType || undefined,
                surroundings: surroundings || undefined,
              }
            : {
                buildingType: watchBuildingType || undefined,
                condition: condition || undefined,
                material: material || undefined,
                objectType: objectType || undefined,
                locationType: locationType || undefined,
                surroundings: surroundings || undefined,
                protectedArea: protectedArea || undefined,
              },
      }),
    );
    trackEvent({
      "dl.eCat": "offer_create",
      "dl.eAct": "bttn.clicked",
      "dl.eLab": "5. krok – Dodatečné údaje",
    }),
      handleNextStep();
  };

  const { handleSubmit, setValue, watch, registerWithError } =
    useForm<IFormValue>(formSchema, {
      keepDataOnSuccess: true,
      defaultValues: {
        ownership: insertReduxData.ownership ?? undefined,
        buildingType: insertReduxData.buildingType ?? undefined,
        condition: insertReduxData.condition ?? "",
        material: insertReduxData.material ?? "",
        flatType: insertReduxData.flatType ?? undefined,
        objectType: insertReduxData.objectType ?? undefined,
        locationType: insertReduxData.locationType ?? undefined,
        surroundings: insertReduxData.surroundings ?? undefined,
        protectedArea: insertReduxData.protectedArea ?? undefined,
      },
    });

  const checkIsBtnDisable = (): boolean => {
    const requiredArray: Array<string | Array<string> | undefined> = [];

    requiredArray.push(
      propertyType === propertyTypeConfig.flat.id
        ? watchOwnership
        : watchBuildingType,
    );

    return requiredArray.some((item) => !item);
  };

  const handleSetValue = useCallback(
    (name: CustomFormValue, value: string) => {
      setValue(name, value);
    },
    [setValue],
  );

  const watchOwnership = watch("ownership");
  const watchBuildingType = watch("buildingType");
  const watchFlatType = watch("flatType");
  const watchObjectType = watch("objectType");

  const watchProtectedArea = watch("protectedArea");

  useEffect(
    () =>
      trackEvent({
        "dl.eCat": "offer_create",
        "dl.eAct": "page.loaded",
        "dl.eLab": "5. krok – Dodatečné údaje",
      }),
    [trackEvent],
  );

  useEffect(() => {
    if (insertReduxData.ownership && !watchOwnership) {
      setValue("ownership", insertReduxData.ownership);
    }
  }, [insertReduxData.ownership, watchOwnership, setValue]);

  useEffect(() => {
    if (insertReduxData.buildingType && !watchBuildingType) {
      setValue("buildingType", insertReduxData.buildingType);
    }
  }, [insertReduxData.buildingType, watchBuildingType, setValue]);

  return (
    <form
      onSubmit={handleSubmit(handleData)}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Box textAlign="left">
        {propertyType === propertyTypeConfig.flat.id ? (
          <SectionCards
            title="Vlastnictví"
            setValue={handleSetValue}
            formWatch={watchOwnership}
            nameValue="ownership"
            items={ownershipConfig}
            isRequired
            titlePaddingProps={{ pt: "0px", pb: "16px" }}
            icon={<IconPeople boxSize="16px" />}
          />
        ) : (
          <SectionCards
            icon={<IconHouse boxSize="16px" />}
            title="Typ stavby"
            setValue={handleSetValue}
            formWatch={watchBuildingType}
            nameValue="buildingType"
            items={buildingTypeConfig}
            numberOfColumns={{ mobile: 1, desktop: 2 }}
            titlePaddingProps={{ pt: "0px", pb: "16px" }}
            isRequired
          />
        )}
        <Grid
          gridTemplateColumns={{
            base: `repeat(1, 1fr)`,
            sm: `repeat(2, 1fr)`,
          }}
        >
          <Box>
            <SectionTitle
              icon={<IconStav boxSize="16px" />}
              title="Stav jednotky"
            />
            <Select
              backgroundColor="ud-white"
              {...registerWithError("condition")}
              icon={<IconDropdown />}
              iconSize="12px"
            >
              <option value="">Vyberte stav</option>
              {Object.entries(buildingConditionConfig).map((item) => (
                <option key={item[0]} value={item[1].id}>
                  {item[1].translationId}
                </option>
              ))}
            </Select>
          </Box>
          <Box pl={{ md: "10px" }}>
            <SectionTitle
              icon={<IconKontrukce boxSize="16px" />}
              title="Typ konstrukce"
              titlePaddingProps={{
                pt: { base: "16px", md: "40px" },
                pb: "16px",
              }}
            />
            <Select
              backgroundColor="ud-white"
              {...registerWithError("material")}
              icon={<IconDropdown />}
              iconSize="12px"
            >
              <option value="">Vyberte typ</option>
              {Object.entries(materialConfig).map((item) => (
                <option key={item[0]} value={item[1].id}>
                  {item[1].translationId}
                </option>
              ))}
            </Select>
          </Box>
        </Grid>
        {propertyType === propertyTypeConfig.flat.id ? (
          <SectionCards
            icon={<IconHouse boxSize="16px" />}
            title="Typ bytu"
            setValue={handleSetValue}
            formWatch={watchFlatType}
            nameValue="flatType"
            items={flatTypeConfig}
            titlePaddingProps={{ pt: { base: "16px", md: "24px" }, pb: "16px" }}
          />
        ) : (
          <SectionCards
            icon={<IconArea boxSize="16px" />}
            title="Typ objektu"
            setValue={handleSetValue}
            formWatch={watchObjectType}
            nameValue="objectType"
            items={objectTypeConfig}
            titlePaddingProps={{ pt: { base: "16px", md: "24px" }, pb: "16px" }}
          />
        )}
        <Grid
          gridTemplateColumns={{
            base: `repeat(1, 1fr)`,
            sm: `repeat(2, 1fr)`,
          }}
          gap={{ base: "10px" }}
        >
          <Box>
            <SectionTitle
              icon={<IconHouse boxSize="16px" />}
              title="Umístění v lokalitě"
            />
            <Select
              backgroundColor="ud-white"
              {...registerWithError("locationType")}
              icon={<IconDropdown />}
              iconSize="12px"
            >
              {Object.entries(locationTypeConfig).map((item) => (
                <option key={item[0]} value={item[1].id}>
                  {item[1].translationId}
                </option>
              ))}
            </Select>
          </Box>
          <Box>
            <SectionTitle
              titlePaddingProps={{
                pb: "16px",
                pt: { base: "16px", md: "40px" },
              }}
              icon={<IconHouse boxSize="16px" />}
              title="Okolní zástavba"
            />
            <Select
              backgroundColor="ud-white"
              {...registerWithError("surroundings")}
              icon={<IconDropdown />}
              iconSize="12px"
            >
              {Object.entries(surroundingsConfig).map((item) => (
                <option key={item[0]} value={item[1].id}>
                  {item[1].translationId}
                </option>
              ))}
            </Select>
          </Box>
        </Grid>
        {propertyType === propertyTypeConfig.house.id && (
          <SectionCards
            icon={<IconDog boxSize="16px" fillWith="ud-grey-600" />}
            title="Ochrana území"
            setValue={handleSetValue}
            formWatch={watchProtectedArea}
            nameValue="protectedArea"
            items={protectedAreaConfig}
            titlePaddingProps={{ pt: "24px", pb: "16px" }}
            numberOfColumns={{ mobile: 1, desktop: 2 }}
          />
        )}
      </Box>
      <StepButtons
        buttonPrevText={buttonPrevText}
        buttonNextText={buttonNextText}
        checkIsBtnDisable={checkIsBtnDisable}
        mt={[null, "24px"]}
      />
    </form>
  );
};
